import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PricingUpdateComponent } from 'src/app/+shared/components/pricing-update.component/pricing-update.component';
import { PaymentType } from 'src/app/model/enums/payment-type';
import { StorageKeys } from 'src/app/model/enums/storage-keys';
import { Fee } from 'src/app/model/interfaces/fee';
import { Order } from 'src/app/model/interfaces/order';
import { ReceiptOrders } from 'src/app/model/interfaces/receipt-orders';
import { Share } from 'src/app/model/interfaces/share';
import { TicketPackageInstance } from 'src/app/model/interfaces/ticket-package-instance';
import { Visitor } from 'src/app/model/visitor';
import { PriceService } from 'src/app/services/price.service';
import { ReceiptService } from 'src/app/services/receipt-service';
import { StorageService } from 'src/app/services/storage.service';
import { VisitorService } from 'src/app/services/visitor.service';

declare interface OrderedPackage { packageInstance: TicketPackageInstance; price: number; }
declare interface OrderedShare { share: Share; price: number; }

@Component({
    selector: 'app-receipt-strip',
    templateUrl: 'receipt-strip.component.html',
    styleUrls: ['receipt-strip.component.scss']
})
export class ReceiptStripComponent implements OnInit, OnDestroy
{
    public visitor: Visitor = {};
    public orders: ReceiptOrders;
    public finalized: boolean;
    public totalPrice: number;
    public view: 'dibs' | 'default' = 'default';
    public orderedShares: OrderedShare[] = [];
    public orderedPackages: OrderedPackage[] = [];
    public salesClassId: number;
    public pricingUpdateExpanded = false;
    public paymentPeriodString: string;
    public subscriptionPaymentType: number;
    public accordionIsOpen = false;
    public accordionGapfundIsOpen = false;
    public allSMTOrderShares: Share[] = []; // Consolidation of ticketshares and ticketsinstances
    public isCardPayment: boolean;

    @ViewChild('receiptPaper')
    public receiptPaper: ElementRef;

    @ViewChild('receiptPrinterHolder')
    public receiptPrinterHolder: ElementRef;

    @ViewChild('pricingUpdate')
    public pricingUpdate: PricingUpdateComponent;

    @Output('renderedHeight') public renderedHeight = new EventEmitter();

    @Input() public showPaymentPeriod: boolean;
    @Input() public nextDrawDate: Date;
    @Input() public paymentPeriod: number;
    @Input() public gapfundDeclined: boolean;
    @Input() public BSFees: Fee[];
    
    private readonly priceService: PriceService;
    private readonly visitorService: VisitorService;
    private readonly receiptService: ReceiptService;

    private lotteryClassByIdSubscription: Subscription;
    private priceSubscriptions: Subscription;

    public constructor(
        priceService: PriceService,
        visitorService: VisitorService,
        receiptService: ReceiptService,
        private storageService:StorageService,
    )
    {
        this.visitorService = visitorService;
        this.priceService = priceService;
        this.receiptService = receiptService;
    }

    public ngOnInit(): void
    {
        this.getOrders();
        this.isCardPayment = this.receiptService.paymentType === PaymentType.Dibs;
    }

    private getOrders(): void
    {
        const orders = this.receiptService.getOrders();
        if(orders)
        {
            this.orders = orders;

            this.handlePaymentDetails();

            if(orders.orderSmt)
            {
                this.concatTicketSharesAndTicketInstances(orders.orderSmt);
            }

            this.buildModel();
        }
    }

    private handlePaymentDetails(): void
    {
        if(this.showPaymentPeriod) {
            if(this.paymentPeriod === 1 ) {
                this.paymentPeriodString = '/md'
            } 
            if(this.paymentPeriod === 6 ) {
                this.paymentPeriodString = '/halvårligt'
            }
        }
        
        this.subscriptionPaymentType = Number(sessionStorage.getItem(StorageKeys.PaymentMethodUsed));
    }

    private concatTicketSharesAndTicketInstances(smtOrder: Order): void
    {
        smtOrder.Shares.forEach((item) => {
            this.allSMTOrderShares.push(item);
        });
        smtOrder.Instances.forEach((item) => {
            this.allSMTOrderShares.push(item.Share);
        });
    }

    private async buildModel(): Promise<void>
    {
        if (!this.orders)
        {
            return;
        }

        const currentOrder: Order = this.orders.orderSubscription === null && this.orders.orderSmt !== null ? this.orders.orderSmt : this.orders.orderSubscription;
        this.priceService.getPriceByOrder(currentOrder).subscribe((priceobj) => {
          if(this.gapfundDeclined && this.subscriptionPaymentType === 4) { // Betalingsservice
            this.totalPrice = this.paymentPeriod === 1 ? priceobj.totalPrice + priceobj.fees[0].value : priceobj.totalPrice;
          } else {
            this.totalPrice = priceobj.pricePrMonth;
          }
        });

        const tasks = [];

        if(this.orders.orderSubscription === null && this.orders.orderSmt !== null)
        {
            this.allSMTOrderShares.map((share: Share) =>
                {
                    tasks.push(this.priceService.getPriceByShareType(share.Type).pipe(tap(p => {
                        this.orderedShares.push(
                            {
                                share: share,
                                price: p.subscriptionSingleMonth
                            }
                        );
                    })));
                }
            );
        }
        else
        {
            this.orders.orderSubscription.Shares.map(
                (share: Share) =>
                {
                    tasks.push(this.priceService.getPriceByShareType(share.Type).pipe(tap(p => {
                        this.orderedShares.push(
                            {
                                share: share,
                                price: p.subscriptionSingleMonth
                            }
                        );
                    })));
                }
            );
        }

        const orderPackageInstances = this.orders.orderSubscription === null && this.orders.orderSmt !== null ? this.orders.orderSmt.TicketPackageInstances : this.orders.orderSubscription.TicketPackageInstances;
        orderPackageInstances.map((packageInstance: TicketPackageInstance) =>
            {
                tasks.push(this.priceService.getPriceByPackage(packageInstance.TicketPackage).pipe(tap(p => {
                    this.orderedPackages.push(
                        {
                            packageInstance,
                            price: p.subscriptionSingleMonth
                        }
                    );
                })));
            }
        );

        this.priceSubscriptions = combineLatest(tasks).subscribe();
        this.visitor = this.visitorService.getVisitor();
        this.finalized = false;

        if(this.orders.orderSubscription === null && this.orders.orderSmt !== null)
        {
            this.salesClassId = this.orders.orderSmt.SubscriptionStartClassId;
        }
        else
        {
            this.salesClassId = this.orders.orderSubscription.SubscriptionStartClassId;   
        }

        setTimeout(
            () =>
            {
                this.finalize();
            }, 0
        );

        // Remove items after use (NgOnDestroy does not destroy the entry)
        setTimeout(() => {
          sessionStorage.removeItem(StorageKeys.ReceiptOrderSubscription);
        }, 5000);
    }

    public finalize(): void
    {
        this.finalized = !this.finalized;

        if (this.finalized)
        {
            this.view = 'default';
            const height = this.receiptPaper.nativeElement.clientHeight;
            if(this.receiptPrinterHolder)this.receiptPrinterHolder.nativeElement.style.height = `${height}px`;
            this.renderedHeight.emit(height);
        }
    }


    public onPricingDetailsClick(): void
    {
        this.pricingUpdate.expand();
    }

    public onPricingUpdateExpand(): void
    {
        this.pricingUpdateExpanded = true;
        if(this.receiptPrinterHolder)this.receiptPrinterHolder.nativeElement.className = this.receiptPrinterHolder.nativeElement.className.replace(' animation', '');
        setTimeout(
            () => {
                
                const height = this.receiptPaper.nativeElement.clientHeight;
                if(this.receiptPrinterHolder)this.receiptPrinterHolder.nativeElement.style.height = `${height}px`;
            }, 
            0
        );
    }

    public toggleAccordion(): void
    {
        this.accordionIsOpen = !this.accordionIsOpen;
    }

    public toggleGapfundAccordion(): void
    {
        this.accordionGapfundIsOpen = !this.accordionGapfundIsOpen;
    }

    public ngOnDestroy(): void 
    {
        if (this.lotteryClassByIdSubscription != null)
        {
            this.lotteryClassByIdSubscription.unsubscribe();
        }

        if (this.priceSubscriptions != null)
        {
            this.priceSubscriptions.unsubscribe();
        }

        this.storageService.removeItem(StorageKeys.GapfundDeclined);
        this.storageService.removeItem(StorageKeys.Paymethod);
        this.storageService.removeItem(StorageKeys.PaymentPeriod);
        this.storageService.removeItem(StorageKeys.PaymentMethodUsed);
    }
}
